import type { ThemeOptions } from '@mui/material/styles';

// Overrides read `theme.vars` so they follow the active colour scheme. Use
// `theme.applyStyles('dark', ...)` for dark-only tweaks rather than checking
// `theme.palette.mode`.
export const components: ThemeOptions['components'] = {
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        fontWeight: 700,
        boxShadow: 'none',
        borderRadius: '50px',
        borderColor: theme.vars.palette.grey[300],
        '&.MuiButton-outlined.MuiButton-colorTertiary': {
          color: theme.vars.palette.grey[900],
        },
        '&.MuiButton-outlined.MuiButton-colorTertiary:hover': {
          borderColor: theme.vars.palette.grey[500],
        },
        '&.MuiButton-contained.MuiButton-colorTertiary:hover': {
          outline: `1px solid ${theme.vars.palette.grey[500]}`,
        },
        '&.MuiButton-text.MuiButton-colorTertiary': {
          color: theme.vars.palette.grey[900],
        },
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontWeight: 500,
        borderRadius: '8px',
        caretColor: 'black',
        background: theme.vars.palette.surface.input,
        '&:hover': {
          background: theme.vars.palette.surface.inputHover,
        },
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
        '&.Mui-disabled': {
          background: `${theme.vars.palette.surface.input} !important`,
        },
        ...theme.applyStyles('dark', {
          caretColor: theme.vars.palette.text.primary,
        }),
        '&.MuiInputBase-colorTertiary': {
          background: theme.vars.palette.surface.raised,
          '> fieldset': {
            border: `1px solid ${theme.vars.palette.grey[400]}`,
          },
          '&:hover': {
            '> fieldset': {
              border: `1px solid ${theme.vars.palette.grey[600]}`,
            },
          },
          '&:focus-within': {
            '> fieldset': {
              border: `1px solid ${theme.vars.palette.grey[600]}`,
            },
          },
        },
      }),
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        background: theme.vars.palette.surface.input,
        '&.Mui-disabled': {
          background: `${theme.vars.palette.surface.input} !important`,
        },
      }),
    },
  },
  // Date fields render their own input instead of InputBase/FilledInput,
  // so mirror the overrides above to keep them consistent with other inputs.
  MuiPickersInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontWeight: 500,
        caretColor: 'black',
        ...theme.applyStyles('dark', {
          caretColor: theme.vars.palette.text.primary,
        }),
      }),
    },
  },
  MuiPickersFilledInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        background: theme.vars.palette.surface.input,
        '&:hover': {
          background: theme.vars.palette.surface.inputHover,
        },
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
        '&.Mui-disabled': {
          background: `${theme.vars.palette.surface.input} !important`,
        },
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: '10px',
        transform: 'none',
        left: '12px',
        top: '6px',
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        letterSpacing: '0.2px',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        letterSpacing: '0.2px',
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: {
        '&.MuiSkeleton-rectangular': {
          borderRadius: '4px',
        },
      },
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: {
        zIndex: 1300,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: 12,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.vars.palette.grey[900],
      }),
    },
  },
};
